import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { v4 } from 'uuid';
import reducer, { ActiveRecordActionType } from '../../components/active-record/active-record.reducer';

interface UseMobileRecordStateProps {
  formId: number;
  recordId?: number;
  formSettingsLoaded: boolean;
}

export const useMobileRecordState = ({ formId, recordId, formSettingsLoaded }: UseMobileRecordStateProps) => {
  const [state, dispatcher] = useReducer(reducer, {
    displayCollapsed: false,
    childSettings: [],
    currentFullScreen: false,
    minimiseActiveRecord: false,
    frozen: false,
  });

  const currentRecordKey = useMemo(() => `${recordId || 'new'}`, [recordId]);

  const addActiveRecord = useCallback(
    (
      recordKey: string,
      icon: string,
      details: string | React.ReactElement,
      fullscreen = false,
      extraSettings?: unknown
    ) => {
      const existingRecord = state.childSettings.find((s) => s.key === recordKey);

      if (existingRecord) {
        if (recordKey === currentRecordKey) {
          const childRecords = state.childSettings.filter((s) => s.key !== currentRecordKey);
          childRecords.forEach((childRecord) => {
            dispatcher({
              type: ActiveRecordActionType.CloseActiveRecord,
              recordKey: childRecord.key,
            });
          });
        } else {
          dispatcher({
            type: ActiveRecordActionType.SetActiveRecord,
            recordKey,
          });
        }
      } else {
        dispatcher({
          type: ActiveRecordActionType.AddActiveRecord,
          activeRecordSetting: {
            key: recordKey,
            renderKey: v4(),
            icon,
            details,
            extraSettings,
          },
          currentFullScreen: fullscreen,
        });
      }
    },
    [state.childSettings, currentRecordKey]
  );

  const onRecordClose = useCallback((recordKey: string) => {
    dispatcher({
      type: ActiveRecordActionType.CloseActiveRecord,
      recordKey,
    });
  }, []);

  const onRecordClick = useCallback((recordKey: string) => {
    dispatcher({
      type: ActiveRecordActionType.SetActiveRecord,
      recordKey,
    });
  }, []);

  useEffect(() => {
    if (!formSettingsLoaded) return;

    const existingRecord = state.childSettings.find((s) => s.key === currentRecordKey);
    if (!existingRecord) {
      dispatcher({
        type: ActiveRecordActionType.AddActiveRecord,
        activeRecordSetting: {
          key: currentRecordKey,
          renderKey: v4(),
          icon: '',
          details: '',
          extraSettings: {
            formId: formId,
            recordId: recordId,
            displayText: '',
          },
        },
        currentFullScreen: false,
      });
    }
  }, [formId, recordId, formSettingsLoaded, currentRecordKey, state.childSettings]);

  useEffect(() => {
    if (recordId) {
      const newRecordKey = 'new';
      const hasNewRecord = state.childSettings.some((s) => s.key === newRecordKey);
      if (hasNewRecord) {
        dispatcher({
          type: ActiveRecordActionType.CloseActiveRecord,
          recordKey: newRecordKey,
        });
      }
    }
  }, [recordId, state.childSettings]);

  return {
    state,
    currentRecordKey,
    addActiveRecord,
    onRecordClose,
    onRecordClick,
  };
};
